import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getUnidades, getModelos, createExecucao } from '../services/api';

/* ==== Ícones (SVG inline) ==== */
const Icon = ({ children, className = 'h-6 w-6' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
       viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
       className={className}>{children}</svg>
);
const MapPin = (p) => <Icon {...p}><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></Icon>;
const CheckCircle = (p) => <Icon {...p}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></Icon>;
const ChevronRight = (p) => <Icon {...p}><polyline points="9 18 15 12 9 6"/></Icon>;
const ChevronLeft = (p) => <Icon {...p}><polyline points="15 18 9 12 15 6"/></Icon>;
const AlertTriangle = (p) => <Icon {...p}><path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" x2="12" y1="9" y2="13"/><line x1="12" x2="12.01" y1="17" y2="17"/></Icon>;
const Search = (p) => <Icon {...p}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></Icon>;

/* ==== Página ==== */
const ExecucoesPage = () => {
  const [step, setStep] = useState(1);

  const [unidades, setUnidades] = useState([]);
  const [modelos, setModelos] = useState([]);

  const [selectedUnidade, setSelectedUnidade] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isStepLoading, setIsStepLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchUnidade, setSearchUnidade] = useState('');
  const [searchModelo, setSearchModelo] = useState('');

  const navigate = useNavigate();

  /* ---- Carrega unidades (passo 1) ---- */
  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await getUnidades();
        setUnidades(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err?.message || 'Erro ao carregar unidades.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  /* ---- Seleção passo 1 -> carrega modelos ---- */
  const handleSelectUnidade = async (unidade) => {
    setSelectedUnidade(unidade);
    setStep(2);
    try {
      setIsStepLoading(true);
      setError(null);
      const data = await getModelos();
      setModelos(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Erro ao carregar modelos.');
    } finally {
      setIsStepLoading(false);
    }
  };

  /* ---- Seleção passo 2 -> cria execução ---- */
  const handleSelectModelo = async (modelo) => {
    try {
      const nova = await createExecucao({
        modelo_id: modelo.modelo_id,
        unidade_id: selectedUnidade.unidade_id,
      });
      navigate(`/checklist/execucoes/${nova.execucao_id}`);
    } catch (err) {
      const msg = err?.message || 'Erro ao iniciar execução.';
      setError(msg);
      toast.error(`Erro ao iniciar execução: ${msg}`);
    }
  };

  /* ---- Listas filtradas (busca) ---- */
  const unidadesFiltradas = useMemo(() => {
    const q = searchUnidade.trim().toLowerCase();
    if (!q) return unidades;
    return unidades.filter(u =>
      (u.nome || '').toLowerCase().includes(q) ||
      (u.cidade || '').toLowerCase().includes(q) ||
      (u.estado || '').toLowerCase().includes(q)
    );
  }, [unidades, searchUnidade]);

  const modelosFiltrados = useMemo(() => {
    const q = searchModelo.trim().toLowerCase();
    if (!q) return modelos;
    return modelos.filter(m =>
      (m.titulo || '').toLowerCase().includes(q) ||
      (m.descricao || '').toLowerCase().includes(q)
    );
  }, [modelos, searchModelo]);

  /* ---- UI auxiliares ---- */
  const EmptyState = ({ title, subtitle }) => (
    <div className="p-6 text-center border border-dashed border-gray-200 rounded-xl bg-gray-50">
      <p className="font-semibold text-gray-800">{title}</p>
      {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
    </div>
  );

  /* ---- Loading geral (inicio) ---- */
  if (isLoading) {
    return (
      <div className="p-6">
        <div className="h-7 w-40 bg-gray-200 rounded mb-5 animate-pulse" />
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow p-6 space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  /* ---- Erro global ---- */
  if (error && step === 1) {
    return (
      <div className="p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-3" />
        <h2 className="text-xl font-bold text-gray-800">Erro ao Carregar</h2>
        <p className="text-gray-600 mt-1">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Título */}
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Aplicar um Checklist</h1>

      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6">
        {/* Stepper */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <span className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-600'}`}>1</span>
            <span className={`text-sm font-medium ${step >= 1 ? 'text-gray-900' : 'text-gray-500'}`}>Selecione a unidade</span>
          </div>
          <div className="flex-1 h-px bg-gray-200 mx-4" />
          <div className="flex items-center gap-3">
            <span className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold ${step === 2 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-600'}`}>2</span>
            <span className={`text-sm font-medium ${step === 2 ? 'text-gray-900' : 'text-gray-500'}`}>Selecione o checklist</span>
          </div>
        </div>

        {/* Passo 1 */}
        <section className={step === 1 ? 'opacity-100' : 'opacity-50'}>
          <div className="flex items-center text-gray-700 mb-3">
            <MapPin className="text-green-500 mr-3" />
            <h2 className="text-lg font-semibold">Passo 1 de 2 — Selecione uma unidade</h2>
          </div>

          {step === 1 && (
            <>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  value={searchUnidade}
                  onChange={(e) => setSearchUnidade(e.target.value)}
                  placeholder="Buscar por nome, cidade ou UF"
                  className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {unidadesFiltradas.length === 0 ? (
                <EmptyState title="Nenhuma unidade encontrada." subtitle="Ajuste o termo de busca ou cadastre uma nova unidade." />
              ) : (
                <ul className="space-y-3">
                  {unidadesFiltradas.map((u) => (
                    <li key={u.unidade_id}>
                      <button
                        onClick={() => handleSelectUnidade(u)}
                        className="w-full flex items-center justify-between p-4 rounded-lg border border-purple-200 bg-gradient-to-br from-purple-100 via-purple-200 to-purple-100 hover:from-purple-200 hover:via-purple-300 hover:to-purple-200 hover:border-purple-500 shadow-sm hover:shadow-md transition"
                      >
                        <div className="text-left">
                          <p className="font-medium text-gray-900">{u.nome}</p>
                          <p className="text-xs text-gray-600">
                            {(u.cidade || '—')}{u.estado ? ` / ${u.estado}` : ''}
                          </p>
                        </div>
                        <ChevronRight className="text-purple-600" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>

        <hr className="my-6" />

        {/* Passo 2 */}
        <section className={step === 2 ? 'opacity-100' : 'opacity-50'}>
          <div className="flex items-center text-gray-700 mb-3">
            <CheckCircle className={`${step === 2 ? 'text-green-500' : 'text-gray-400'} mr-3`} />
            <h2 className="text-lg font-semibold">Passo 2 de 2 — Selecione um checklist</h2>
          </div>

          {step === 2 && (
            <>
              <div className="flex items-center justify-between mb-3">
                <button
                  onClick={() => setStep(1)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border m-2 text-sm hover:bg-gray-50"
                >
                  <ChevronLeft className="h-4 w-4" /> Voltar
                </button>
                {selectedUnidade && (
                  <div className="text-xs text-gray-500">
                    Unidade selecionada: <span className="font-medium text-gray-700">{selectedUnidade.nome}</span>
                  </div>
                )}
              </div>

              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  value={searchModelo}
                  onChange={(e) => setSearchModelo(e.target.value)}
                  placeholder="Buscar por título ou descrição do checklist"
                  className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {isStepLoading ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
                  ))}
                </div>
              ) : modelosFiltrados.length === 0 ? (
                <EmptyState title="Nenhum modelo encontrado." subtitle="Ajuste a busca ou crie um novo modelo." />
              ) : (
                <ul className="space-y-3">
                  {modelosFiltrados.map((m) => (
                    <li key={m.modelo_id}>
                      <button
                        onClick={() => handleSelectModelo(m)}
                        className="w-full flex items-center justify-between p-4 rounded-lg border border-purple-200 bg-gradient-to-br from-purple-100 via-purple-200 to-purple-100 hover:from-purple-200 hover:via-purple-300 hover:to-purple-200 hover:border-purple-500 shadow-sm hover:shadow-md transition"
                      >
                        <div className="text-left">
                          <p className="font-medium text-gray-900">{m.titulo}</p>
                          {m.descricao && <p className="text-xs text-gray-600">{m.descricao}</p>}
                        </div>
                        <ChevronRight className="text-purple-600" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default ExecucoesPage;